import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { ENVIRONMENT, Environment, ValidationError } from '@netfoundry-ui/shared/model';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { likeContains } from './frontdoor-v3-execution.service';
import { checkFrontdoorBody } from './frontdoor-v3-validation';
import { LoggerService } from './logger.service';
import { HTTP_CLIENT } from './token.service';

/**
 * An IPsec share as the v3 API returns it: the share's own fields, plus the derived `access`.
 *
 * Its own resource rather than a tunnel field on a TCP share, because it carries no mTLS ingress — no listener, no
 * enrolled identity, no AWS stack. A client inside the customer network reaches it at the tunnel's intercept IP.
 */
export interface IpsecShareV3 {
    [key: string]: any;

    id: string;
    frontdoorId?: string;
    /** Agents that serve the share's traffic, balanced between them. */
    activeAgentIds?: string[];
    /** Agents that serve the share's traffic only when no active agent is bound. */
    passiveAgentIds?: string[];
    /** The tunnel that reaches this share. Its edge router is the one identity the dial policy names. */
    ipsecTunnelId?: string;
    name?: string;
    description?: string;
    /**
     * Where every hosting agent forwards this share's traffic. The address is an IPv4 or IPv6 literal or a hostname of
     * any TLD, because the host sits inside a customer network. Not a CIDR block — a share reaches one host.
     */
    targetAddress?: string;
    targetPort?: number;
    /**
     * Port a client connects to at the tunnel's intercept IP. Unique within the tunnel, because every share on it is
     * intercepted at that one address.
     */
    ingressPort?: number;
    /** The address a client connects to, built by the service from the intercept IP and the port. */
    access?: string;
    /** Id of the Ziti service backing this share. Absent until the service has been projected. */
    zitiServiceId?: string;
    /**
     * How far the share has been built: `NEW`, `DEPLOYED`, `ERROR`, `DELETING` or `DELETED`. Read it through
     * {@link ipsecShareStatus} rather than directly.
     */
    status?: string;
    createdAt?: string;
    updatedAt?: string;
}

/**
 * A request to create an IPsec share. Nothing is provisioned outside Ziti, so the answer already carries `DEPLOYED`.
 *
 * Every field but `description` and `passiveAgentIds` is required. `ingressPort` in particular: a TCP share has one allocated for it, and an
 * IPsec share does not, because the port is the only thing telling two shares on one tunnel apart.
 */
export interface CreateIpsecShareV3 {
    frontdoorId: string;
    /** At least one. */
    activeAgentIds: string[];
    passiveAgentIds?: string[];
    /** Must belong to the same account and be `PROVISIONED`: one still building has no edge router to name. */
    ipsecTunnelId: string;
    name: string;
    targetAddress: string;
    targetPort: number;
    ingressPort: number;
    description?: string;
}

/**
 * A request to change an IPsec share. Only the fields the body carries are applied: a sent list replaces the stored
 * one, and `null` clears `passiveAgentIds` and `description`.
 *
 * Pointing a share at another tunnel rewrites its dial policy and keeps its address. It is also how a share left in
 * `ERROR` by a deleted tunnel is brought back.
 */
export interface UpdateIpsecShareV3 {
    name?: string;
    activeAgentIds?: string[];
    passiveAgentIds?: string[] | null;
    ipsecTunnelId?: string;
    targetAddress?: string;
    targetPort?: number;
    ingressPort?: number;
    description?: string | null;
}

/** The longest name the service accepts, from ZitiUtil.MAX_SHARE_NAME_LENGTH. */
export const MAX_IPSEC_SHARE_NAME_LENGTH = 168;

/**
 * The ingress port range the service accepts, and the one port inside it that it does not: the bridge's metrics
 * exporter holds 9814. Server configuration, so this is the hint a form shows and never the arbiter.
 */
export const IPSEC_INGRESS_PORT_MIN = 1024;
export const IPSEC_INGRESS_PORT_MAX = 65535;
export const IPSEC_INGRESS_PORT_EXCLUDED = 9814;

/** One page of shares, plus the link that gets the next one. */
export interface IpsecSharePageV3 {
    shares: IpsecShareV3[];
    /** The service's own link to the next page, or '' at the end. Opaque — nothing here parses it. */
    nextHref: string;
    /** Total across all pages when the service reports one, otherwise null. */
    total: number | null;
}

export const DEFAULT_IPSEC_SHARE_PAGE_SIZE = 50;

/**
 * Enough shares to count what every tunnel of one frontdoor reaches in a single read. A read that still fell short says
 * so through the page's `total`, so a caller can report partial counts.
 */
export const IPSEC_SHARE_COUNT_PAGE_SIZE = 500;

/**
 * What a share listing can be narrowed by at the service. Only what `FindIpsecShare` binds, so a field here is one the
 * API answers on rather than one this end drops rows for.
 */
export interface IpsecShareListFilters {
    /**
     * Matched as a contains, case-insensitively — one value is read as a `LIKE`, so this is a search box's value and
     * not an exact name.
     */
    name?: string;
    /** The agents whose shares to list, in either role. Several are read as "any of these". */
    agentIds?: string[];
    /** The tunnels whose shares to list. Several are read as "any of these". */
    ipsecTunnelIds?: string[];
    /** The statuses to list, in the spelling the wire carries them in. Several are read as "any of these". */
    statuses?: string[];
}

/** Adds the filters a listing was asked for. An absent or empty one is left off entirely. */
export function withIpsecShareFilters(params: HttpParams, filters: IpsecShareListFilters = {}): HttpParams {
    let withFilters = params;
    const name = (filters.name ?? '').trim();
    if (name.length > 0) {
        withFilters = withFilters.set('name', likeContains(name));
    }
    // Repeated rather than joined: each parameter binds a set, and one comma-joined value would be read as a single
    // name nothing is called.
    withFilters = appendAll(withFilters, 'agentId', filters.agentIds);
    withFilters = appendAll(withFilters, 'ipsecTunnelId', filters.ipsecTunnelIds);
    withFilters = appendAll(withFilters, 'status', filters.statuses);
    return withFilters;
}

/**
 * How far a share has been built. Five states come from the service; `unknown` is this end of the wire, for a share
 * that carries no status or one this does not know.
 *
 * There is no `pendingDeploy`: nothing is provisioned outside Ziti, so a create answers `deployed` rather than
 * reaching it minutes later.
 */
export type IpsecShareStatus = 'new' | 'deployed' | 'error' | 'deleting' | 'deleted' | 'unknown';

/** How a status reads on screen: the badge tone that carries it, and the words in it. */
export interface IpsecShareStatusBadge {
    tone: 'ok' | 'warn' | 'err' | 'muted';
    label: string;
}

const IPSEC_SHARE_STATUS_BADGES: Record<IpsecShareStatus, IpsecShareStatusBadge> = {
    new: { tone: 'warn', label: 'New' },
    deployed: { tone: 'ok', label: 'Deployed' },
    error: { tone: 'err', label: 'Error' },
    deleting: { tone: 'warn', label: 'Deleting' },
    deleted: { tone: 'muted', label: 'Deleted' },
    unknown: { tone: 'muted', label: 'Unknown' },
};

/** The service's own names for those states, in the spelling the wire carries them in. */
const IPSEC_SHARE_WIRE_STATUSES = new Map<string, IpsecShareStatus>([
    ['NEW', 'new'],
    ['DEPLOYED', 'deployed'],
    ['ERROR', 'error'],
    ['DELETING', 'deleting'],
    ['DELETED', 'deleted'],
]);

/** The statuses a filter offers, in the order a reader meets them. `deleted` is left out: the listing never shows one. */
export const IPSEC_SHARE_STATUS_OPTIONS: IpsecShareStatus[] = ['new', 'deployed', 'error', 'deleting'];

/**
 * Read the status off a share. Anything unsent or unrecognised is `unknown`, so a state added at the far end reads as
 * "not known here" rather than as one of the five.
 */
export function ipsecShareStatus(share: IpsecShareV3 | undefined): IpsecShareStatus {
    return IPSEC_SHARE_WIRE_STATUSES.get((share?.status ?? '').toUpperCase()) ?? 'unknown';
}

/** How to draw `status`. One place, because the listing and the drawer must not disagree. */
export function ipsecShareStatusBadge(status: IpsecShareStatus): IpsecShareStatusBadge {
    return IPSEC_SHARE_STATUS_BADGES[status] ?? IPSEC_SHARE_STATUS_BADGES.unknown;
}

/** The wire spelling of a status, for a filter the service applies. */
export function ipsecShareWireStatus(status: IpsecShareStatus): string {
    for (const [wire, known] of IPSEC_SHARE_WIRE_STATUSES) {
        if (known === status) {
            return wire;
        }
    }
    return '';
}

/**
 * Reads IPsec shares from the v3 frontdoor API. One frontdoor at a time: every path is scoped by the frontdoor id, so
 * the caller has to have chosen one before this is worth calling.
 *
 * **Every endpoint is gated on the network extension, reads included.** A network without `IPSEC_TUNNELER` answers 404
 * here rather than an empty page, so a caller should ask {@link FrontdoorExtensionAvailabilityService} before it asks this.
 */
@Injectable({ providedIn: 'root' })
export class FrontdoorV3IpsecShareService {
    private readonly apiUrl: string;

    constructor(
        private logger: LoggerService,
        @Inject(HTTP_CLIENT) private http: HttpClient,
        @Inject(ENVIRONMENT) private environment: Environment
    ) {
        this.apiUrl = this.environment.frontdoorURL;
    }

    /**
     * One page of `frontdoorId`'s IPsec shares, newest first unless `sort` says otherwise. The service pages by offset,
     * so `page` counts from 0 and the page's `total` counts every match. `filters` narrows the listing at the service.
     */
    public findIpsecShares(
        frontdoorId: string,
        sort = 'createdAt,DESC',
        size = DEFAULT_IPSEC_SHARE_PAGE_SIZE,
        filters: IpsecShareListFilters = {},
        page = 0
    ): Observable<IpsecSharePageV3> {
        const params = withIpsecShareFilters(
            new HttpParams().set('sort', sort).set('size', String(size)).set('page', String(page)),
            filters
        );
        return this.listShares(frontdoorId, params);
    }

    /**
     * The shares reached over any of `ipsecTunnelIds`, in one read. The parameter binds a set, so a page of tunnels
     * costs one request rather than one per row, and the caller groups the answer by each share's `ipsecTunnelId`. An
     * empty list of ids asks nothing and answers an empty page.
     */
    public findSharesForTunnels(
        frontdoorId: string,
        ipsecTunnelIds: string[],
        size = IPSEC_SHARE_COUNT_PAGE_SIZE
    ): Observable<IpsecSharePageV3> {
        const ids = ipsecTunnelIds.filter((id) => !!id);
        if (ids.length === 0) {
            return of({ shares: [], nextHref: '', total: 0 });
        }
        const params = withIpsecShareFilters(new HttpParams().set('sort', 'name,ASC').set('size', String(size)), {
            ipsecTunnelIds: ids,
        });
        return this.listShares(frontdoorId, params);
    }

    /**
     * One IPsec share of `frontdoorId`. A share addressed under another account answers 404, so a stale link is a miss
     * rather than a read across accounts.
     */
    public getIpsecShare(frontdoorId: string, shareId: string): Observable<IpsecShareV3> {
        return this.http
            .get(`${this.apiUrl}/v3/${frontdoorId}/ipsec-shares/${shareId}`, { headers: this.headers() })
            .pipe(map((response: any) => response as IpsecShareV3));
    }

    /**
     * Adds an IPsec share to `frontdoorId`. The body repeats the id because the service rejects a body that names a
     * different account, and the caller should not be the one to get that wrong.
     *
     * The answer already carries `DEPLOYED`: the agent binds the Ziti service and the tunnel's edge router dials it, so
     * there is no second half to wait for.
     */
    public createIpsecShare(frontdoorId: string, share: CreateIpsecShareV3): Observable<IpsecShareV3> {
        const body = { ...share, frontdoorId };
        return this.http
            .post(`${this.apiUrl}/v3/${frontdoorId}/ipsec-shares`, body, { headers: this.headers() })
            .pipe(map((response: any) => response as IpsecShareV3));
    }

    /**
     * Whether {@link createIpsecShare} would be accepted, without creating anything. Sends the same verb, path and body
     * as the write, so the check is about the request the caller would make. Authorized identically, so a caller
     * without create still gets a 403.
     */
    public validateCreateIpsecShare(frontdoorId: string, share: CreateIpsecShareV3): Observable<ValidationError[]> {
        const body = { ...share, frontdoorId };
        return checkFrontdoorBody(this.http, `${this.apiUrl}/v3/${frontdoorId}/ipsec-shares`, body);
    }

    /**
     * Whether an update would be accepted, without changing anything. See {@link validateCreateIpsecShare}.
     *
     * Checks with `PUT` where the write is a `PATCH`, because the method picks the rules and a `PUT` checks the share as
     * it would end up. So the caller must send a complete body: `name`, `activeAgentIds`, `ipsecTunnelId`,
     * `targetAddress`, `targetPort` and `ingressPort` are reported missing otherwise.
     */
    public validateUpdateIpsecShare(
        frontdoorId: string,
        shareId: string,
        update: UpdateIpsecShareV3
    ): Observable<ValidationError[]> {
        return checkFrontdoorBody(this.http, `${this.apiUrl}/v3/${frontdoorId}/ipsec-shares/${shareId}`, update, 'PUT');
    }

    /**
     * Applies `update` to one IPsec share. `PATCH`, so only the fields the body carries are applied: a rename renames
     * every Ziti object, a new target rewrites the `host.v1` config, new host lists rewrite the bind policy, a new port
     * rewrites the intercept config, and a new tunnel rewrites the dial policy.
     */
    public updateIpsecShare(
        frontdoorId: string,
        shareId: string,
        update: UpdateIpsecShareV3
    ): Observable<IpsecShareV3> {
        return this.http
            .patch(`${this.apiUrl}/v3/${frontdoorId}/ipsec-shares/${shareId}`, update, { headers: this.headers() })
            .pipe(map((response: any) => response as IpsecShareV3));
    }

    /**
     * Deletes the share and the Ziti objects behind it, so it stops answering. Its ingress port goes back to the
     * tunnel, where another share can then take it.
     */
    public deleteIpsecShare(frontdoorId: string, shareId: string): Observable<any> {
        return this.http.delete(`${this.apiUrl}/v3/${frontdoorId}/ipsec-shares/${shareId}`, {
            headers: this.headers(),
        });
    }

    /**
     * The next page, by following the link the service returned. Taken verbatim, because the cursor inside it only
     * resumes under the order that made it.
     */
    public followNext(nextHref: string): Observable<IpsecSharePageV3> {
        return this.http
            .get(nextHref, { headers: this.headers() })
            .pipe(map((response: any) => this.readPage(response)));
    }

    private listShares(frontdoorId: string, params: HttpParams): Observable<IpsecSharePageV3> {
        return this.http
            .get(`${this.apiUrl}/v3/${frontdoorId}/ipsec-shares`, { headers: this.headers(), params })
            .pipe(map((response: any) => this.readPage(response)));
    }

    private headers(): HttpHeaders {
        return new HttpHeaders().set('Accept', 'application/hal+json');
    }

    /** Accepts the HAL collection, a bare array, or a Spring page, so the shape can settle later. */
    private readPage(response: any): IpsecSharePageV3 {
        if (Array.isArray(response)) {
            return { shares: response, nextHref: '', total: response.length };
        }
        const shares = this.readList(response);
        const total = Number.isFinite(response?.page?.totalElements) ? response.page.totalElements : null;
        return { shares, nextHref: response?._links?.next?.href ?? '', total };
    }

    private readList(response: any): IpsecShareV3[] {
        const embedded = response?._embedded;
        if (embedded) {
            const collection = embedded.ipsecShareList ?? embedded.ipsecShares ?? Object.values(embedded)[0];
            if (Array.isArray(collection)) {
                return collection;
            }
        }
        if (Array.isArray(response?.content)) {
            return response.content;
        }
        this.logger.error('findIpsecShares: unrecognised response shape', response);
        return [];
    }
}

/** Repeats `name` once per value, dropping the empty ones. */
function appendAll(params: HttpParams, name: string, values: string[] | undefined): HttpParams {
    let withValues = params;
    for (const value of values ?? []) {
        if (value) {
            withValues = withValues.append(name, value);
        }
    }
    return withValues;
}
